import {
  faChevronsLeft,
  faChevronsRight,
  faGear
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import type React from 'react'
import { type ReactElement, useContext, useEffect, useState } from 'react'
import { Shimmer } from '~/components/Global/Elements/Shimmer/Shimmer'
import { CreateOrderModal } from '~/domains/orders/components/CreateOrderModal'
import { themeClass } from '~/styles/theme.css'
import Logo from '../../../../img/logo-square.svg'
import {
  isLegacySettingsPage,
  SidebarSwitchContext
} from '../../../routes/_dashboard/-components/sidebar'
import {
  extendTrigger,
  footer,
  footerExtended,
  footerLogo,
  placeholder,
  wrapper,
  wrapperExtended
} from './GlobalMenu.css'
import { GlobalMenuItem } from './GlobalMenuItem'
import type { MenuItemPrimary } from './menu'

interface GlobalMenuProps {
  menu?: MenuItemPrimary[]
  currentSection?: string
  legacyUrl?: string | null
  mainRef?: React.RefObject<HTMLDivElement>
}

export const GlobalMenu = ({
  menu,
  currentSection,
  legacyUrl,
  mainRef
}: GlobalMenuProps): ReactElement => {
  const { setShowDashboardMenu } = useContext(SidebarSwitchContext)
  const isOnSettingsPage =
    currentSection?.startsWith('/settings') || isLegacySettingsPage
  const lowerScreenSize = 1500
  const extendedMenuSize = 260
  const lowMainSize = lowerScreenSize - extendedMenuSize
  const [canExtend, setCanExtend] = useState<boolean>(
    window.innerWidth > lowerScreenSize
  )
  const [extendedMenu, setExtendedMenu] = useState<boolean>(
    localStorage.getItem('extendedMenu') !== 'false' && canExtend
  )
  const [createResourceType, setCreateResourceType] = useState<
    'order' | 'recourse' | null
  >(null)

  useEffect(() => {
    const handleOpenCreateModal = (event: CustomEvent) => {
      const { resourceType } = event.detail
      setCreateResourceType(resourceType)
    }

    document.addEventListener(
      'openCreateOrderModal',
      handleOpenCreateModal as EventListener
    )

    return () => {
      document.removeEventListener(
        'openCreateOrderModal',
        handleOpenCreateModal as EventListener
      )
    }
  }, [])

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      if (mainRef?.current) {
        const autoExtend = localStorage.getItem('extendedMenu') !== 'false'
        const isExtendable =
          mainRef.current.offsetWidth >=
          (extendedMenu ? lowMainSize : lowerScreenSize)
        const shouldAutoHide = mainRef.current.offsetWidth < lowMainSize
        setCanExtend(isExtendable)
        if (shouldAutoHide) {
          setExtendedMenu(false)
        } else if (isExtendable && autoExtend) {
          setExtendedMenu(true)
        }
      }
    })
    // On legacy we use window size
    const handleResize = () => {
      const screenEnoughWide = window.innerWidth > lowerScreenSize
      setCanExtend(screenEnoughWide)
      setExtendedMenu(
        localStorage.getItem('extendedMenu') !== 'false' && screenEnoughWide
      )
    }
    if (mainRef?.current) {
      resizeObserver.observe(mainRef.current)
    } else {
      window.addEventListener('resize', handleResize)
    }
    return () => {
      if (mainRef?.current) {
        resizeObserver.disconnect()
      } else {
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [mainRef, lowMainSize, extendedMenu])

  const toggleExtendedMenu = () => {
    setExtendedMenu(!extendedMenu)
    localStorage.setItem('extendedMenu', (!extendedMenu).toString())
  }

  return (
    <nav
      className={clsx(
        themeClass,
        wrapper,
        extendedMenu ? wrapperExtended : null
      )}
    >
      {isOnSettingsPage && (
        <GlobalMenuItem
          menuItem={{
            id: 'settings',
            label: 'Retour aux préférences',
            icon: faGear,
            onClick: (e) => {
              e.preventDefault()
              setShowDashboardMenu(false)
            }
          }}
          currentSection={'settings'}
          legacyUrl={legacyUrl}
          extendedMenu={extendedMenu}
        />
      )}
      {menu?.map((item) => (
        <GlobalMenuItem
          key={item.id}
          menuItem={item}
          currentSection={currentSection ?? null}
          legacyUrl={legacyUrl}
          extendedMenu={extendedMenu}
          onOrderCreateClick={() => setCreateResourceType('order')}
          onRecourseCreateClick={() => setCreateResourceType('recourse')}
        />
      ))}
      {menu === undefined &&
        [...new Array(8)].map((_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: Shimmer cant have unique key
          <div className={placeholder} key={index}>
            <Shimmer width={35} height={35} />
          </div>
        ))}
      <div className={clsx(footer, extendedMenu ? footerExtended : null)}>
        <img src={Logo} alt="" className={footerLogo} />
        {canExtend && (
          <span onClick={toggleExtendedMenu} className={extendTrigger}>
            <FontAwesomeIcon
              icon={extendedMenu ? faChevronsLeft : faChevronsRight}
            />
          </span>
        )}
      </div>
      {createResourceType && (
        <CreateOrderModal
          open
          onClose={() => setCreateResourceType(null)}
          resourceType={createResourceType}
        />
      )}
    </nav>
  )
}
